/**
 * TemplateManagement.tsx — Finding Templates admin page.
 *
 * Full CRUD for finding_templates + AI mode toggle (super_admin only).
 * Accessible to engineer and super_admin roles.
 */

import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import API_BASE from '../../config/api';
import { useAuth } from '../../context/AuthContext';

import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Divider from '@mui/material/Divider';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Select from '@mui/material/Select';
import Snackbar from '@mui/material/Snackbar';
import Switch from '@mui/material/Switch';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';

import AddIcon from '@mui/icons-material/Add';
import ArticleIcon from '@mui/icons-material/Article';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import ErrorIcon from '@mui/icons-material/Error';
import PsychologyIcon from '@mui/icons-material/Psychology';
import RefreshIcon from '@mui/icons-material/Refresh';
import SearchIcon from '@mui/icons-material/Search';
import SyncIcon from '@mui/icons-material/Sync';

// ── Types ─────────────────────────────────────────────────────────────────────

interface Template {
  id: string;
  prowler_check_id: string;
  check_name: string;
  provider: string;
  severity: string;
  explanation: string;
  fix_instructions: string;
  business_impact: string | null;
  compliance_frameworks: string | null;
  is_active: boolean;
  created_at: string | null;
  updated_at: string | null;
}

interface AIConfig {
  ai_mode: string;
  claude_api_configured: boolean;
  notes: string | null;
  updated_at: string | null;
}

const PROVIDERS = ['all', 'aws', 'gcp', 'azure', 'ssh'];
const SEVERITIES = ['critical', 'high', 'medium', 'low'];

const SEVERITY_COLORS: Record<string, 'error' | 'warning' | 'info' | 'default'> = {
  critical: 'error',
  high: 'warning',
  medium: 'info',
  low: 'default',
};

const PROVIDER_COLORS: Record<string, string> = {
  aws:   '#FF9900',
  gcp:   '#4285F4',
  azure: '#0078D4',
  ssh:   '#6D4C41',
  all:   '#555',
};

// ── Empty form state ──────────────────────────────────────────────────────────

const EMPTY_FORM = {
  prowler_check_id: '',
  check_name: '',
  provider: 'aws',
  severity: 'medium',
  explanation: '',
  fix_instructions: '',
  business_impact: '',
  compliance_frameworks: '',
  is_active: true,
};

// ── Component ─────────────────────────────────────────────────────────────────

export default function TemplateManagement() {
  const { user } = useAuth();
  const isSuperAdmin = user?.role === 'super_admin';
  const canEdit   = isSuperAdmin || user?.role === 'engineer';
  const canManage = isSuperAdmin; // add, delete, sync, AI mode

  const [templates,    setTemplates]    = useState<Template[]>([]);
  const [aiConfig,     setAiConfig]     = useState<AIConfig | null>(null);
  const [loading,      setLoading]      = useState(true);
  const [search,       setSearch]       = useState('');
  const [filterProvider, setFilterProvider] = useState('');
  const [filterSeverity, setFilterSeverity] = useState('');

  const [dialogOpen,   setDialogOpen]   = useState(false);
  const [editTemplate, setEditTemplate] = useState<Template | null>(null);
  const [form,         setForm]         = useState({ ...EMPTY_FORM });
  const [saving,       setSaving]       = useState(false);
  const [formError,    setFormError]    = useState('');

  const [deleteTarget, setDeleteTarget] = useState<Template | null>(null);
  const [deleting,     setDeleting]     = useState(false);

  const [aiModeChanging, setAiModeChanging]  = useState(false);
  const [aiModeConfirm,  setAiModeConfirm]   = useState(false);
  const [pendingMode,    setPendingMode]      = useState('');

  const [toast, setToast] = useState<{ msg: string; severity: 'success' | 'error' } | null>(null);

  // Prowler sync
  const [syncPreview,    setSyncPreview]    = useState<any>(null);
  const [syncRunning,    setSyncRunning]    = useState(false);
  const [syncResult,     setSyncResult]     = useState<any>(null);
  const [showSyncDialog, setShowSyncDialog] = useState(false);

  // ── Load data ───────────────────────────────────────────────────────────────

  const loadTemplates = useCallback(async () => {
    const params: Record<string, string> = {};
    if (filterProvider) params.provider = filterProvider;
    if (filterSeverity) params.severity = filterSeverity;
    if (search)         params.search   = search;

    const res = await axios.get<Template[]>(`${API_BASE}/templates`, { params });
    setTemplates(res.data);
  }, [filterProvider, filterSeverity, search]);

  const loadAiConfig = useCallback(async () => {
    const res = await axios.get<AIConfig>(`${API_BASE}/templates/ai-mode/config`);
    setAiConfig(res.data);
  }, []);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        await Promise.all([loadTemplates(), loadAiConfig()]);
      } catch {
        setToast({ msg: 'Failed to load templates', severity: 'error' });
      } finally {
        setLoading(false);
      }
    })();
  }, [loadTemplates, loadAiConfig]);

  // ── Stats ───────────────────────────────────────────────────────────────────

  const totalCount  = templates.length;
  const activeCount = templates.filter(t => t.is_active).length;
  const inactiveCount = totalCount - activeCount;

  // ── Dialog helpers ──────────────────────────────────────────────────────────

  function openCreate() {
    setEditTemplate(null);
    setForm({ ...EMPTY_FORM });
    setFormError('');
    setDialogOpen(true);
  }

  function openEdit(t: Template) {
    setEditTemplate(t);
    setForm({
      prowler_check_id:    t.prowler_check_id,
      check_name:          t.check_name,
      provider:            t.provider,
      severity:            t.severity,
      explanation:         t.explanation,
      fix_instructions:    t.fix_instructions,
      business_impact:     t.business_impact ?? '',
      compliance_frameworks: t.compliance_frameworks ?? '',
      is_active:           t.is_active,
    });
    setFormError('');
    setDialogOpen(true);
  }

  async function handleSave() {
    if (!form.prowler_check_id.trim() || !form.check_name.trim() ||
        !form.explanation.trim() || !form.fix_instructions.trim()) {
      setFormError('Prowler Check ID, Check Name, Explanation, and Fix Instructions are required.');
      return;
    }
    setSaving(true);
    setFormError('');
    try {
      const payload = {
        prowler_check_id:    form.prowler_check_id.trim(),
        check_name:          form.check_name.trim(),
        provider:            form.provider,
        severity:            form.severity,
        explanation:         form.explanation.trim(),
        fix_instructions:    form.fix_instructions.trim(),
        business_impact:     form.business_impact.trim() || null,
        compliance_frameworks: form.compliance_frameworks.trim() || null,
        is_active:           form.is_active,
      };

      if (editTemplate) {
        await axios.patch(`${API_BASE}/templates/${editTemplate.id}`, payload);
        setToast({ msg: 'Template updated', severity: 'success' });
      } else {
        await axios.post(`${API_BASE}/templates`, payload);
        setToast({ msg: 'Template created', severity: 'success' });
      }
      setDialogOpen(false);
      await loadTemplates();
    } catch (err: any) {
      const msg = err?.response?.data?.detail ?? 'Save failed';
      setFormError(typeof msg === 'string' ? msg : JSON.stringify(msg));
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete() {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await axios.delete(`${API_BASE}/templates/${deleteTarget.id}`);
      setToast({ msg: 'Template deleted', severity: 'success' });
      setDeleteTarget(null);
      await loadTemplates();
    } catch {
      setToast({ msg: 'Delete failed', severity: 'error' });
    } finally {
      setDeleting(false);
    }
  }

  async function handleActiveToggle(t: Template) {
    try {
      await axios.patch(`${API_BASE}/templates/${t.id}`, { is_active: !t.is_active });
      await loadTemplates();
    } catch {
      setToast({ msg: 'Update failed', severity: 'error' });
    }
  }

  // ── AI Mode ─────────────────────────────────────────────────────────────────

  function requestModeChange(newMode: string) {
    if (!canManage) return;
    if (newMode === 'claude_api' && !aiConfig?.claude_api_configured) {
      setToast({
        msg: 'ANTHROPIC_API_KEY is not configured. Add it to .env before switching to Claude API mode.',
        severity: 'error',
      });
      return;
    }
    setPendingMode(newMode);
    setAiModeConfirm(true);
  }

  async function confirmModeChange() {
    setAiModeChanging(true);
    try {
      const res = await axios.patch<AIConfig>(`${API_BASE}/templates/ai-mode/config`, {
        ai_mode: pendingMode,
      });
      setAiConfig(res.data);
      setToast({ msg: `AI mode switched to ${pendingMode}`, severity: 'success' });
    } catch {
      setToast({ msg: 'Failed to update AI mode', severity: 'error' });
    } finally {
      setAiModeChanging(false);
      setAiModeConfirm(false);
      setPendingMode('');
    }
  }

  // ── Copy to clipboard ────────────────────────────────────────────────────────

  function copyCheckId(id: string) {
    navigator.clipboard.writeText(id).then(() =>
      setToast({ msg: 'Check ID copied', severity: 'success' })
    );
  }

  // ── Prowler Sync ──────────────────────────────────────────────────────────────

  async function loadSyncPreview() {
    setSyncResult(null);
    setSyncPreview(null);
    try {
      const { data } = await axios.get(`${API_BASE}/templates/sync/preview`);
      setSyncPreview(data);
      setShowSyncDialog(true);
    } catch {
      setToast({ msg: 'Failed to load sync preview', severity: 'error' });
    }
  }

  async function runSync() {
    setSyncRunning(true);
    setSyncResult(null);
    try {
      const { data } = await axios.post(
        `${API_BASE}/templates/sync/run`,
        {},
        { timeout: 600000 }
      );
      setSyncResult(data);
      await loadTemplates();
      setToast({
        msg: `Sync complete — Added: ${data.summary.added}, Updated: ${data.summary.updated}`,
        severity: 'success',
      });
    } catch (err: any) {
      const msg = err?.response?.data?.detail ?? 'Sync failed';
      setToast({ msg, severity: 'error' });
    } finally {
      setSyncRunning(false);
    }
  }

  // ── Render ────────────────────────────────────────────────────────────────────

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
        <ArticleIcon sx={{ color: 'primary.main', fontSize: 32 }} />
        <Box>
          <Typography variant="h5" fontWeight={700}>Finding Templates</Typography>
          <Typography variant="body2" color="text.secondary">
            {loading ? 'Loading…' : `${totalCount} templates — ${activeCount} active, ${inactiveCount} inactive`}
          </Typography>
        </Box>
      </Box>

      {/* Info banner for non-super_admin roles */}
      {!canManage && (
        <Alert severity="info" sx={{ mb: 2 }}>
          You can view and edit template content (explanation, fix instructions, business impact).
          Only Super Admin can add, delete, or sync templates with Prowler.
        </Alert>
      )}

      {/* AI Mode card — super_admin only */}
      {canManage && aiConfig && (
        <Card elevation={2} sx={{ mb: 3, borderLeft: 4, borderColor: aiConfig.ai_mode === 'claude_api' ? 'secondary.main' : 'primary.main' }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, mb: 1.5 }}>
              <PsychologyIcon sx={{ color: 'primary.main', mt: 0.25 }} />
              <Typography variant="subtitle1" fontWeight={700}>AI Explanation Mode</Typography>
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
              <Typography
                variant="body2"
                fontWeight={aiConfig.ai_mode === 'hybrid' ? 700 : 400}
                color={aiConfig.ai_mode === 'hybrid' ? 'primary.main' : 'text.secondary'}
              >
                Hybrid Mode
              </Typography>
              <Switch
                checked={aiConfig.ai_mode === 'claude_api'}
                onChange={(e) => requestModeChange(e.target.checked ? 'claude_api' : 'hybrid')}
                disabled={aiModeChanging}
                color="secondary"
              />
              <Typography
                variant="body2"
                fontWeight={aiConfig.ai_mode === 'claude_api' ? 700 : 400}
                color={aiConfig.ai_mode === 'claude_api' ? 'secondary.main' : 'text.secondary'}
              >
                Claude API Mode
              </Typography>

              <Box sx={{ ml: 1, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                {aiConfig.claude_api_configured ? (
                  <CheckCircleIcon sx={{ fontSize: 16, color: 'success.main' }} />
                ) : (
                  <ErrorIcon sx={{ fontSize: 16, color: 'error.main' }} />
                )}
                <Typography variant="caption" color="text.secondary">
                  Claude API Key: {aiConfig.claude_api_configured ? 'Configured' : 'Not configured'}
                </Typography>
              </Box>
            </Box>

            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              {aiConfig.ai_mode === 'hybrid'
                ? 'Hybrid: Use templates first, Claude API as fallback for unknown checks.'
                : 'Claude API: Always use Claude API for all explanations (requires API key).'}
            </Typography>
          </CardContent>
        </Card>
      )}

      {/* Toolbar */}
      <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
        <TextField
          size="small"
          placeholder="Search by check ID or name…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
          sx={{ minWidth: 280 }}
        />

        <FormControl size="small" sx={{ minWidth: 130 }}>
          <InputLabel>Provider</InputLabel>
          <Select value={filterProvider} label="Provider" onChange={(e) => setFilterProvider(e.target.value)}>
            <MenuItem value="">All</MenuItem>
            {PROVIDERS.filter(p => p !== 'all').map(p => (
              <MenuItem key={p} value={p}>{p.toUpperCase()}</MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl size="small" sx={{ minWidth: 130 }}>
          <InputLabel>Severity</InputLabel>
          <Select value={filterSeverity} label="Severity" onChange={(e) => setFilterSeverity(e.target.value)}>
            <MenuItem value="">All</MenuItem>
            {SEVERITIES.map(s => (
              <MenuItem key={s} value={s} sx={{ textTransform: 'capitalize' }}>{s}</MenuItem>
            ))}
          </Select>
        </FormControl>

        <Box sx={{ flexGrow: 1 }} />

        <Tooltip title="Refresh template list">
          <IconButton onClick={() => loadTemplates()} disabled={loading}>
            <RefreshIcon />
          </IconButton>
        </Tooltip>

        {canManage && (
          <Button
            variant="outlined"
            color="secondary"
            startIcon={syncRunning ? <CircularProgress size={16} color="inherit" /> : <SyncIcon />}
            onClick={loadSyncPreview}
            disabled={syncRunning}
          >
            {syncRunning ? 'Syncing…' : 'Sync with Prowler'}
          </Button>
        )}

        {canManage && (
          <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}>
            Add Template
          </Button>
        )}
      </Box>

      {/* Table */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer component={Paper} elevation={2}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ '& th': { fontWeight: 700 } }}>
                <TableCell>Prowler Check ID</TableCell>
                <TableCell>Check Name</TableCell>
                <TableCell>Provider</TableCell>
                <TableCell>Severity</TableCell>
                <TableCell align="center">Active</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {templates.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                    No templates found
                  </TableCell>
                </TableRow>
              ) : (
                templates.map((t) => (
                  <TableRow key={t.id} hover>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Typography
                          variant="body2"
                          fontFamily="monospace"
                          sx={{ fontSize: '0.78rem', maxWidth: 280, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                          title={t.prowler_check_id}
                        >
                          {t.prowler_check_id}
                        </Typography>
                        <Tooltip title="Copy check ID">
                          <IconButton size="small" onClick={() => copyCheckId(t.prowler_check_id)}>
                            <ContentCopyIcon sx={{ fontSize: 13 }} />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{t.check_name}</Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={t.provider.toUpperCase()}
                        size="small"
                        sx={{
                          fontWeight: 700,
                          fontSize: '0.7rem',
                          bgcolor: PROVIDER_COLORS[t.provider] ?? '#888',
                          color: '#fff',
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={t.severity}
                        size="small"
                        color={SEVERITY_COLORS[t.severity] ?? 'default'}
                        sx={{ textTransform: 'capitalize', fontWeight: 600 }}
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Switch
                        checked={t.is_active}
                        size="small"
                        onChange={() => handleActiveToggle(t)}
                        color="success"
                      />
                    </TableCell>
                    <TableCell align="right">
                      {canEdit && (
                        <Tooltip title="Edit">
                          <IconButton size="small" onClick={() => openEdit(t)}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                      {canManage && (
                        <Tooltip title="Delete">
                          <IconButton size="small" color="error" onClick={() => setDeleteTarget(t)}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* ── Add/Edit Dialog ──────────────────────────────────────────────────── */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle fontWeight={700}>
          {editTemplate ? 'Edit Template' : 'Add Template'}
        </DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            {formError && <Alert severity="error">{formError}</Alert>}

            <TextField
              label="Prowler Check ID *"
              value={form.prowler_check_id}
              onChange={(e) => setForm(f => ({ ...f, prowler_check_id: e.target.value }))}
              disabled={!!editTemplate}
              placeholder="e.g. s3_bucket_public_access_block"
              size="small"
              inputProps={{ style: { fontFamily: 'monospace' } }}
              helperText={editTemplate ? 'Set by Prowler — cannot be changed' : undefined}
            />

            <TextField
              label="Check Name *"
              value={form.check_name}
              onChange={(e) => setForm(f => ({ ...f, check_name: e.target.value }))}
              size="small"
              placeholder="e.g. S3 Bucket Public Access Block"
              disabled={!!editTemplate && !canManage}
              helperText={editTemplate && !canManage ? 'Set by Prowler — Super Admin only' : undefined}
            />

            <Box sx={{ display: 'flex', gap: 2 }}>
              <FormControl size="small" sx={{ flex: 1 }} disabled={!!editTemplate && !canManage}>
                <InputLabel>Provider</InputLabel>
                <Select value={form.provider} label="Provider" onChange={(e) => setForm(f => ({ ...f, provider: e.target.value }))}>
                  {PROVIDERS.map(p => <MenuItem key={p} value={p}>{p.toUpperCase()}</MenuItem>)}
                </Select>
              </FormControl>

              <FormControl size="small" sx={{ flex: 1 }} disabled={!!editTemplate && !canManage}>
                <InputLabel>Severity</InputLabel>
                <Select value={form.severity} label="Severity" onChange={(e) => setForm(f => ({ ...f, severity: e.target.value }))}>
                  {SEVERITIES.map(s => <MenuItem key={s} value={s} sx={{ textTransform: 'capitalize' }}>{s}</MenuItem>)}
                </Select>
              </FormControl>
            </Box>

            <TextField
              label="Explanation *"
              value={form.explanation}
              onChange={(e) => setForm(f => ({ ...f, explanation: e.target.value }))}
              multiline
              rows={4}
              size="small"
              placeholder="Plain-English explanation for a non-technical business owner…"
            />

            <TextField
              label="Fix Instructions *"
              value={form.fix_instructions}
              onChange={(e) => setForm(f => ({ ...f, fix_instructions: e.target.value }))}
              multiline
              rows={5}
              size="small"
              helperText="Use numbered steps: 1. Go to… 2. Click…"
            />

            <TextField
              label="Business Impact"
              value={form.business_impact}
              onChange={(e) => setForm(f => ({ ...f, business_impact: e.target.value }))}
              multiline
              rows={2}
              size="small"
              placeholder="What happens if this is not fixed…"
            />

            <TextField
              label="Compliance Frameworks"
              value={form.compliance_frameworks}
              onChange={(e) => setForm(f => ({ ...f, compliance_frameworks: e.target.value }))}
              size="small"
              placeholder="SOC2,GDPR,HIPAA,PCI-DSS,ISO27001"
              helperText="Comma-separated framework names"
            />

            <FormControlLabel
              control={
                <Switch
                  checked={form.is_active}
                  onChange={(e) => setForm(f => ({ ...f, is_active: e.target.checked }))}
                  color="success"
                />
              }
              label="Active (inactive templates fall back to Claude API)"
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setDialogOpen(false)} disabled={saving}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={saving}
            startIcon={saving ? <CircularProgress size={16} color="inherit" /> : null}
          >
            {saving ? 'Saving…' : editTemplate ? 'Save Changes' : 'Create Template'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Delete Confirmation Dialog ───────────────────────────────────────── */}
      <Dialog open={!!deleteTarget} onClose={() => setDeleteTarget(null)} maxWidth="xs" fullWidth>
        <DialogTitle fontWeight={700}>Delete Template?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            Are you sure you want to delete the template for{' '}
            <strong style={{ fontFamily: 'monospace' }}>{deleteTarget?.prowler_check_id}</strong>?
            This cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setDeleteTarget(null)} disabled={deleting}>Cancel</Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleDelete}
            disabled={deleting}
            startIcon={deleting ? <CircularProgress size={16} color="inherit" /> : null}
          >
            {deleting ? 'Deleting…' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── AI Mode Confirmation Dialog ──────────────────────────────────────── */}
      <Dialog open={aiModeConfirm} onClose={() => setAiModeConfirm(false)} maxWidth="xs" fullWidth>
        <DialogTitle fontWeight={700}>Switch AI Mode?</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 1.5 }}>
            This will affect all customers immediately.
          </Alert>
          <Typography variant="body2">
            Switch to <strong>{pendingMode === 'claude_api' ? 'Claude API Mode' : 'Hybrid Mode'}</strong>?
            {pendingMode === 'claude_api'
              ? ' All finding explanations will be generated by Claude API, ignoring templates.'
              : ' Templates will be used first, with Claude API as fallback.'}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setAiModeConfirm(false)} disabled={aiModeChanging}>Cancel</Button>
          <Button
            variant="contained"
            color="warning"
            onClick={confirmModeChange}
            disabled={aiModeChanging}
            startIcon={aiModeChanging ? <CircularProgress size={16} color="inherit" /> : null}
          >
            {aiModeChanging ? 'Switching…' : 'Confirm Switch'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Prowler Sync Dialog ─────────────────────────────────────────────── */}
      <Dialog
        open={showSyncDialog}
        onClose={() => { if (!syncRunning) setShowSyncDialog(false); }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <SyncIcon color="primary" />
            <Typography fontWeight={700}>Sync with Prowler</Typography>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          {syncPreview && !syncResult && !syncRunning && (
            <>
              <Alert severity="info" sx={{ mb: 2 }}>
                This generates plain-English explanations for all new Prowler checks
                using Claude API. Results are stored permanently — customers never
                trigger AI calls during normal usage.
              </Alert>
              <Table size="small" sx={{ mb: 1.5 }}>
                <TableBody>
                  <TableRow>
                    <TableCell>Current templates in Shield</TableCell>
                    <TableCell><strong>{syncPreview.current_templates}</strong></TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>Prowler checks found</TableCell>
                    <TableCell><strong>{syncPreview.prowler_checks_found}</strong></TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>New checks to add</TableCell>
                    <TableCell>
                      <strong style={{ color: '#2ea55e' }}>
                        {syncPreview.new_checks_to_add}
                      </strong>
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>Estimated Claude API cost</TableCell>
                    <TableCell><strong>${syncPreview.estimated_claude_cost_usd}</strong></TableCell>
                  </TableRow>
                </TableBody>
              </Table>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1 }}>
                {Object.entries(syncPreview.checks_by_provider ?? {}).map(([p, count]) => (
                  <Chip
                    key={p}
                    label={`${p.toUpperCase()}: ${count as number}`}
                    size="small"
                    sx={{ fontWeight: 700 }}
                  />
                ))}
              </Box>
              {!syncPreview.claude_api_configured && (
                <Alert severity="error" sx={{ mt: 1.5 }}>
                  ANTHROPIC_API_KEY is not configured. Add it to .env before syncing.
                </Alert>
              )}
            </>
          )}

          {syncRunning && (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <CircularProgress size={52} />
              <Typography mt={2} color="text.secondary">
                Syncing Prowler checks and generating templates…
                <br />This may take several minutes.
              </Typography>
            </Box>
          )}

          {syncResult && (
            <Alert severity="success">
              <Typography variant="subtitle2" gutterBottom>Sync Complete!</Typography>
              <Typography variant="body2">Added: <strong>{syncResult.summary.added}</strong></Typography>
              <Typography variant="body2">Updated: <strong>{syncResult.summary.updated}</strong></Typography>
              <Typography variant="body2">Unchanged: <strong>{syncResult.summary.unchanged}</strong></Typography>
              <Typography variant="body2">Deactivated: <strong>{syncResult.summary.deactivated}</strong></Typography>
              {syncResult.summary.failed > 0 && (
                <Typography variant="body2" color="error.dark">
                  Failed: <strong>{syncResult.summary.failed}</strong>
                </Typography>
              )}
              <Typography variant="body2" fontWeight={700} mt={1}>
                Total templates: {syncResult.summary.total_templates}
              </Typography>
            </Alert>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setShowSyncDialog(false)} disabled={syncRunning}>
            {syncResult ? 'Close' : 'Cancel'}
          </Button>
          {!syncResult && (
            <Button
              variant="contained"
              onClick={runSync}
              disabled={syncRunning || !syncPreview?.claude_api_configured}
              startIcon={syncRunning ? <CircularProgress size={16} color="inherit" /> : <SyncIcon />}
            >
              {syncRunning ? 'Syncing…' : 'Start Sync'}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* ── Toast ────────────────────────────────────────────────────────────── */}
      <Snackbar
        open={!!toast}
        autoHideDuration={4000}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        {toast ? (
          <Alert severity={toast.severity} onClose={() => setToast(null)} variant="filled">
            {toast.msg}
          </Alert>
        ) : undefined}
      </Snackbar>
    </Box>
  );
}
